// ── FeesCard ──────────────────────────────────────────────────────────────────
// Amount breakdown: line items (charged, tip, tax, fees).
// No computed Net line — fee semantics are ambiguous for settlement math.

import type { Transaction } from "@/application/money/types";
import { formatCurrency } from "@/application/money/types";

interface LineItemProps {
  label: string;
  qualifier?: string;
  value: string;
}

function LineItem({ label, qualifier, value }: LineItemProps) {
  return (
    <div className="flex items-baseline justify-between border-b py-2.5 last:border-b-0">
      <span className="text-foreground text-sm">
        {label}
        {qualifier && (
          <span className="text-subtle-foreground ml-1.5 text-xs">
            ({qualifier})
          </span>
        )}
      </span>
      <span className="text-foreground font-mono text-sm font-medium">
        {value}
      </span>
    </div>
  );
}

interface FeesCardProps {
  transaction: Transaction;
}

export function FeesCard({ transaction: tx }: FeesCardProps) {
  const currency = tx.currency ?? null;

  return (
    <div className="bg-card rounded-lg border p-5">
      <h2 className="text-foreground mb-3 text-sm font-semibold">
        Amount Breakdown
      </h2>

      <LineItem label="Charged" value={formatCurrency(tx.amount, currency)} />

      {tx.tipAmount != null && tx.tipAmount > 0 && (
        <LineItem
          label="Tip"
          value={formatCurrency(Math.round(tx.tipAmount), currency)}
        />
      )}

      {tx.tax != null && (
        <LineItem
          label={tx.tax.taxName ?? "Tax"}
          value={formatCurrency(Math.round(tx.tax.taxAmount), currency)}
        />
      )}

      {tx.processingFee != null && tx.processingFee > 0 && (
        <LineItem
          label="Processing fee"
          qualifier="merchant surcharge passed to customer"
          value={formatCurrency(Math.round(tx.processingFee), currency)}
        />
      )}

      {tx.customFeeAmount != null && tx.customFeeAmount > 0 && (
        <LineItem
          label={tx.customFeeName || "Custom fee"}
          qualifier="merchant fee passed to customer"
          value={formatCurrency(Math.round(tx.customFeeAmount), currency)}
        />
      )}

      {tx.applicationFee != null && tx.applicationFee > 0 && (
        <LineItem
          label="Application fee"
          qualifier="platform fee"
          value={formatCurrency(Math.round(tx.applicationFee), currency)}
        />
      )}
    </div>
  );
}
